import { useCallback, useEffect, useState } from 'react';
import { requestModelVersionDetail, requetModelVersionsByIdList, requestModelCollect } from '@shakkerai/services';
import { notifySuccess } from '@shakkerai/pro-components';
import { translate } from '@shakkerai/util-i18n';
import { BASETYPE_VALUE_TO_NAME } from '../constants';
import { mc } from '@shakkerai/report';

const cachedModelDetail = {};
const cachedModelsByIds = {};

export const requestRelatedVersions = async (versionIds) => {
  if (!Array.isArray(versionIds) || !versionIds.length) {
    return [];
  }

  const key = versionIds.join(',');

  if (cachedModelsByIds[key]) {
    return cachedModelsByIds[key];
  }

  try {
    const res = await requetModelVersionsByIdList(versionIds.map(Number));

    if (res.code === 0 && Array.isArray(res.data)) {
      const models = res.data.map((item) => ({
        baseType: BASETYPE_VALUE_TO_NAME[item.baseType],
        imageUrl: item.cover,
        name: item.modelName,
        modelId: item.modelId,
        uuid: item.uuid,
        versionId: item.id,
        versionName: item.modelVersionName,
      }));

      return (cachedModelsByIds[key] = models);
    }

    return [];
  } catch (error) {
    console.error('[DEBUG] error', error);
    return [];
  }
};

export const useModelVersionIntroDetail = (versionIntroString) => {
  const [versionIntro, setVersionIntro] = useState({ loading: true });

  useEffect(() => {
    try {
      if (versionIntroString) {
        const versionIntroData = JSON.parse(versionIntroString);
        versionIntroData.loading = false;
        setVersionIntro(versionIntroData);

        requestRelatedVersions(versionIntroData.ckpt).then((relatedCheckpoints) => {
          setVersionIntro((data) => {
            return {
              ...data,
              relatedCheckpoints,
            };
          });
        });
      }
    } catch (error) {
      console.error('[DEBUG]', error);
    }
  }, [versionIntroString]);

  return versionIntro;
};

export const useModelDetail = (versionUuid) => {
  const [loading, setLoading] = useState(false);
  const [modelDetail, setModelDetail] = useState(cachedModelDetail[versionUuid]);

  useEffect(() => {
    if (!cachedModelDetail[versionUuid]) {
      setLoading(true);
      requestModelVersionDetail(versionUuid)
        .then((res) => {
          if (res.code === 0 && res.data) {
            const modelData = res.data;
            cachedModelDetail[versionUuid] = modelData;
            setModelDetail(modelData);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setModelDetail(cachedModelDetail[versionUuid]);
    }
  }, [versionUuid]);

  return { loading, modelDetail };
};

export const useModelFavorite = (versionData) => {
  const initialState = versionData?.isLike === 1;
  const [favorited, setFavorited] = useState(initialState);

  useEffect(() => {
    setFavorited(initialState);
  }, [initialState]);

  // type: 0 unsubscribe 1: subscribe

  const favoriteModel = useCallback(
    async (type, source) => {
      const newCollectState = !favorited;

      if (cachedModelDetail[versionData?.uuid]) {
        cachedModelDetail[versionData?.uuid].isLike = newCollectState ? 1 : 0;
      }

      const response = await requestModelCollect({
        uuid: versionData?.uuid,
        type,
      });

      mc('tool.model.card.collect', {
        model_version_uuid: versionData?.uuid,
        area: source || 'card',
      });

      if (response.code === 0) {
        setFavorited(newCollectState);

        if (newCollectState) {
          notifySuccess(translate('model_fav_toast'));
        } else {
          notifySuccess(translate('model_unfav_toast'));
        }
      }
    },
    [versionData?.uuid, favorited],
  );

  return {
    favorited,
    favoriteModel,
  };
};
